import { PublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import config from '$/config';

let globalMsalInstance: PublicClientApplication | null = null;
let redirectResponsePromise: Promise<AuthenticationResult | null> | null = null;

/**
 * Get a safe redirect URI from sessionStorage, ensuring it's valid and within the same origin.
 */
function getRedirectUri(): string | null {
  // Get redirectUri from sessionStorage if available and valid
  const uri = sessionStorage.getItem('msal_redirect_uri');
  if (!uri) return null;
  try {
    const redirectUrl = new URL(uri);
    const currentOrigin = window.location.origin;
    // Only allow redirects within the same origin
    if (redirectUrl.origin !== currentOrigin) return null;
    return uri;
  } catch {
    // Invalid URL format
    return null;
  }
}

export async function initializeGlobalMsalHandler(): Promise<void> {
  if (typeof window === 'undefined') return;

  const redirectUri = getRedirectUri();
  // Skip if already initializing/initialized || no redirectUri
  if (globalMsalInstance || !redirectUri) {
    return;
  }

  const msalConfig = {
    auth: {
      clientId: config.MS_AUTH_CLIENT_ID,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri,
    },
  };

  globalMsalInstance = new PublicClientApplication(msalConfig);
  await globalMsalInstance.initialize();

  // Call handleRedirectPromise immediately and cache the result
  // This MUST be called exactly once to consume the auth code
  redirectResponsePromise = globalMsalInstance.handleRedirectPromise();
}

export async function getRedirectResponse(): Promise<AuthenticationResult | null> {
  if (!redirectResponsePromise) {
    await initializeGlobalMsalHandler();
  }
  return redirectResponsePromise || null;
}

export function getGlobalMsalInstance(): PublicClientApplication | null {
  return globalMsalInstance;
}

export function clearMsalCache(): void {
  if (!globalMsalInstance) return;

  globalMsalInstance.clearCache();
  // Reset cached redirect response promise
  redirectResponsePromise = null;
}

export function resetMsalInstance(): void {
  globalMsalInstance = null;
  redirectResponsePromise = null;
}
